import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import PageHeader from '../components/common/PageHeader';
import { workbenchService, SubfinderScanResult } from '../services/workbenchService';

const WorkbenchSubfinderPage: React.FC = () => {
  const theme = useTheme();
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SubfinderScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const canScan = domain.trim().length > 0 && !loading;

  const handleScan = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await workbenchService.subfinderScan(domain.trim());
      setResult(data);
    } catch {
      setError('Scan fehlgeschlagen. Bitte Domain prüfen und erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.subdomains.length) return;
    try {
      await navigator.clipboard.writeText(result.subdomains.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard might be blocked */ }
  };

  const handleExportCsv = () => {
    if (!result?.subdomains.length) return;
    const csv = 'Subdomain\n' + result.subdomains.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subfinder_${result.domain}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader
        title="Subfinder"
        subtitle="Subdomain-Enumeration für passive Reconnaissance"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'Subfinder' },
        ]}
      />

      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 700, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Domain"
          placeholder="z.B. example.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          fullWidth
          size="small"
        />

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <TravelExploreIcon />}
          disabled={!canScan}
          onClick={handleScan}
          sx={{ alignSelf: 'flex-start', fontWeight: 700, px: 4, minWidth: 180 }}
        >
          {loading ? 'Suche Subdomains...' : 'Scan starten'}
        </Button>

        {error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}
      </Paper>

      {result && (
        <Paper sx={{ mt: 3, borderRadius: 2, maxWidth: 900, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              bgcolor: result.success
                ? `${theme.palette.success.main}18`
                : `${theme.palette.error.main}18`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {result.count} Subdomain{result.count !== 1 ? 's' : ''} gefunden für {result.domain}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Als CSV exportieren">
                <IconButton size="small" onClick={handleExportCsv} disabled={result.count === 0}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={copied ? 'Kopiert!' : 'Alle kopieren'}>
                <IconButton size="small" onClick={handleCopy} disabled={result.count === 0}>
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {result.subdomains.length > 0 ? (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>#</TableCell>
                    <TableCell>Subdomain</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.subdomains.map((sub, idx) => (
                    <TableRow key={sub} hover>
                      <TableCell sx={{ color: theme.palette.text.secondary }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{sub}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: theme.palette.text.secondary }}>
                Keine Subdomains gefunden.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchSubfinderPage;
